import Button from '~components/ui/button'
import Icon from '~components/ui/icon'
import { VetFinder } from '~models/consultation/types'
import { cn } from '~utils/cn'
import Mixpanel from '~utils/mixpanel'
import { openInPlace } from '~utils/open-external'

const VARIANT_CSS: Record<
  VetFinder['variant'],
  { card: string; iconBox: string; icon: string; priority: 'primary' | 'secondary' | 'destructive' }
> = {
  visit: {
    card: '',
    iconBox: 'bg-primary-50 dark:bg-gray-800',
    icon: 'fill-primary-600 dark:fill-primary-400',
    priority: 'primary',
  },
  monitor: {
    card: '',
    iconBox: 'bg-gray-100 dark:bg-gray-800',
    icon: 'fill-gray-700 dark:fill-gray-300',
    priority: 'secondary',
  },
  emergency: {
    card: 'border border-red-100 dark:border-red-500',
    iconBox: 'bg-red-50 dark:bg-gray-800',
    icon: 'fill-red-600 dark:fill-red-500',
    priority: 'destructive',
  },
}

/**
 * drtail.us/vets in SmartThings mode (?client=smartthings): no site header,
 * Maps and website links open in place. Same window, since this webview
 * ignores new windows; the SmartThings back button returns here.
 */
const toSmartThingsUrl = (url: string) => {
  try {
    const parsed = new URL(url)
    parsed.searchParams.set('client', 'smartthings')

    return parsed.toString()
  } catch {
    return url
  }
}

type Props = Readonly<{
  vetFinder: VetFinder
  consultationId: number
}>

/** The server's vet_finder block (DRT-7237), drawn as given. */
const VetFinderCard = ({ vetFinder, consultationId }: Props) => {
  const css = VARIANT_CSS[vetFinder.variant] ?? VARIANT_CSS.visit

  return (
    <section
      aria-label={vetFinder.title}
      className={cn(
        'flex flex-col flex-gap-y-3 rounded-2xl bg-white p-4 shadow-elevation-3 dark:bg-gray-900',
        css.card
      )}
    >
      <div className="flex items-start flex-gap-x-3">
        <div
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-xl',
            css.iconBox
          )}
        >
          <Icon
            name={vetFinder.variant === 'emergency' ? 'emergency_sm' : 'location_sm'}
            className={cn('size-6', css.icon)}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col flex-gap-y-1">
          <h2 className="text-title-3 text-gray-900 dark:text-white">{vetFinder.title}</h2>
          <p className="text-body-3 text-gray-700 dark:text-gray-500">{vetFinder.body}</p>
        </div>
      </div>
      <Button
        className={cn(
          'w-full',
          vetFinder.variant === 'monitor' && 'dark:border-primary-400 dark:text-primary-400'
        )}
        size="medium"
        priority={css.priority}
        onClick={() => {
          Mixpanel.logEvent('vet_finder: clicked', {
            consultation_id: consultationId,
            variant: vetFinder.variant,
            source: 'question',
            platform: 'smartthings',
          })
          openInPlace(toSmartThingsUrl(vetFinder.url))
        }}
      >
        {vetFinder.cta}
      </Button>
    </section>
  )
}

export default VetFinderCard
